/**
 * Trustpilot Review Summary - Product rating summary from metafields; optional popup and scroll-to-reviews.
 */

class TrustpilotReviewSummary extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.showPopup = false;
    this.showTotal = false;
    this.showAverage = true;
    this.productUrl = '#';
    this._timeouts = [];
    this.closeTimeout = null;
  }

  connectedCallback() {
    if (this.hasAttribute('data-initialized')) return;
    try {
      this.init();
      this.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('Trustpilot Review Summary connectedCallback:', error);
    }
  }

  init() {
    const productId = this.getAttribute('data-product-id');
    if (!productId) return;

    /** Get metafield value directly from data attribute */
    const reviewMetafield = this.getAttribute('data-review-summary-metadata');
    if (!reviewMetafield) return;

    try {
      /** Parse pipe-separated string format: averageRating=3.7|totalReviews=154|1star=6|2star=3|3star=2|4star=12|5star=131 */
      const reviewMetaData = this.parseReviewSummaryMetaData(reviewMetafield);
      if (!reviewMetaData || !reviewMetaData.totalReviews || reviewMetaData.totalReviews <= 0) return;

      this.data = {
        averageRating: reviewMetaData.averageRating || 0,
        totalReviews: reviewMetaData.totalReviews || 0,
        numberOfReviews: {
          "5": reviewMetaData.numberOfReviews["5"] || 0,
          "4": reviewMetaData.numberOfReviews["4"] || 0,
          "3": reviewMetaData.numberOfReviews["3"] || 0,
          "2": reviewMetaData.numberOfReviews["2"] || 0,
          "1": reviewMetaData.numberOfReviews["1"] || 0
        }
      };

      this.showPopup = this.getAttribute('data-show-popup') === 'true';
      this.showTotal = this.getAttribute('data-show-total') === 'true';
      this.showAverage = this.getAttribute('data-show-average') !== 'false';
      this.productUrl = this.getAttribute('data-product-url') || '#';

      this.render();
      this.attachEventListeners();
    } catch (e) {
      console.error('Trustpilot Review Summary init error:', e);
    }
  }

  /** Parse pipe-separated metafield (averageRating=3.7|totalReviews=154|1star=6|...) into object. */
  parseReviewSummaryMetaData(str) {
    if (!str || typeof str !== 'string') return null;

    const result = {
      averageRating: 0,
      totalReviews: 0,
      numberOfReviews: { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
    };

    const pairs = str.split('|');
    pairs.forEach(pair => {
      const [key, value] = pair.split('=').map(s => s.trim());
      if (key && value !== undefined) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          if (key === 'averageRating') {
            result.averageRating = numValue;
          } else if (key === 'totalReviews') {
            result.totalReviews = numValue;
          } else if (key.endsWith('star')) {
            /** Support format: 1star, 2star, 3star, 4star, 5star */
            const starKey = key.replace('star', '');
            if (['1', '2', '3', '4', '5'].includes(starKey)) {
              result.numberOfReviews[starKey] = numValue;
            }
          }
        }
      }
    });

    return result;
  }

  render() {
    if (!this.data) return;

    const { averageRating, totalReviews, numberOfReviews } = this.data;

    const starsHTML = this.buildReviewStars(averageRating);
    const textHTML = this.buildReviewText(averageRating, totalReviews);
    const popupHTML = this.showPopup && totalReviews > 0 ? this.buildReviewPopup(totalReviews, numberOfReviews) : '';
    const triggerHTML = this.showPopup
      ? `<button type="button" class="review-summary-trigger" aria-expanded="false" aria-haspopup="dialog" aria-label="Product review: ${averageRating.toFixed(1)} out of 5 stars based on ${totalReviews} reviews">
          ${starsHTML}
          ${textHTML || ''}
         </button>`
      : `${starsHTML}${textHTML || ''}`;

    this.innerHTML = `<div class="review-container">
                        ${triggerHTML}
                        ${popupHTML || ''}
                        <span class="visually-hidden">Product review: ${averageRating.toFixed(1)} out of 5 stars based on ${totalReviews} reviews</span>
                      </div>`;
  }

  buildReviewStars(rating) {
    let html = '';
    let starsHTML = '';

    const fullStars = Math.floor(rating);
    const hasPartial = rating % 1 !== 0;
    const partialPercent = (rating % 1) * 100;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        starsHTML += `<span class="star star-filled">
                        <svg focusable="false" xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 25 25">
                          <polygon points="25 9.12 15.5669599 9.12 12.512219 0 9.40860215 9.12 0 9.12 7.55131965 14.856 4.47214076 24 12.512219 18.216 20.5522972 24 17.4731183 14.856" style="fill: #2e7c94 !important;"></polygon>
                        </svg>
                      </span>`;
      } else if (i === fullStars + 1 && hasPartial) {
        const gradientId = `gradient_${Date.now()}_${i}`;
        starsHTML += `<span class="star star-partial">
                        <svg focusable="false" xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 25 25">
                          <defs>
                            <linearGradient id="${gradientId}" x1="${partialPercent}%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" style="stop-color: rgb(46, 124, 148); stop-opacity: 1;"></stop>
                              <stop offset="1%" style="stop-color: rgb(0, 0, 0); stop-opacity: 1;"></stop>
                            </linearGradient>
                          </defs>
                          <polygon points="25 9.12 15.5669599 9.12 12.512219 0 9.40860215 9.12 0 9.12 7.55131965 14.856 4.47214076 24 12.512219 18.216 20.5522972 24 17.4731183 14.856" style="fill: url(&quot;#${gradientId}&quot;) !important;"></polygon>
                        </svg>
                      </span>`;
      } else {
        starsHTML += `<span class="star star-empty">
                        <svg focusable="false" xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 25 25">
                          <polygon points="25 9.12 15.5669599 9.12 12.512219 0 9.40860215 9.12 0 9.12 7.55131965 14.856 4.47214076 24 12.512219 18.216 20.5522972 24 17.4731183 14.856" style="fill: #000000 !important;"></polygon>
                        </svg>
                      </span>`;
      }
    }
    
    html = `<div class="stars-container" aria-hidden="true">
              ${starsHTML}
            </div>`;

    return html;
  }

  buildReviewText(averageRating, totalReviews) {
    let totalHTML = '';
    const averageHTML = `<span class="average-review">${averageRating.toFixed(1)}</span>`;

    if (this.showTotal) {
      totalHTML = `<span class="total-review">(${totalReviews})</span>`;
    }

    return `<span class="review-summary-text" aria-hidden="true">
              ${averageHTML}
              ${totalHTML}
            </span>`;
  }

  buildReviewPopup(totalReviews, numberOfReviews) {
    let html = '';
    let listHTML = '';

    for (let star = 5; star >= 1; star--) {
      const count = numberOfReviews[star] || 0;
      const percentage = totalReviews > 0 ? Math.max(0, Math.min(100, (count / totalReviews) * 100)) : 0;

      listHTML += `<li class="distribution-item">
                      <span class="visually-hidden">${star} star: ${count} reviews out of ${totalReviews} total reviews</span>
                      <div class="distribution-review" aria-hidden="true">
                        <span class="distribution-review-number">${star}</span>
                        <span class="distribution-review-star">
                          <svg focusable="false" xmlns="http://www.w3.org/2000/svg" width="17px" height="17px" viewBox="0 0 25 25">
                            <polygon points="25 9.12 15.5669599 9.12 12.512219 0 9.40860215 9.12 0 9.12 7.55131965 14.856 4.47214076 24 12.512219 18.216 20.5522972 24 17.4731183 14.856" style="fill: #000 !important;"></polygon>
                          </svg>
                        </span>
                      </div>
                      <div class="distribution-bar-container" aria-hidden="true">
                        <div class="distribution-bar-fill" style="width: ${percentage}%"></div>
                      </div>
                      <span class="distribution-review-count" aria-hidden="true">${count}</span>
                    </li>`;
    }
    
    html = `<dialog class="review-summary-popup" aria-label="Review distribution" aria-modal="true" role="dialog">
              <ul class="review-distribution">
                ${listHTML}
              </ul>
              <button type="button" class="read-reviews-button btn btn-black" aria-label="Read ${totalReviews} reviews, Skip to reviews section" data-skip-reviews>
                Read ${totalReviews} reviews
              </button>
            </dialog>`;

    return html;
  }

  /** Popup open/close, focus trap, "Skip to reviews" scroll + focus. */
  attachEventListeners() {
    const container = this.querySelector('.review-container');
    const trigger = this.querySelector('.review-summary-trigger') || container;
    const popup = this.querySelector('.review-summary-popup');
    if (!container || !popup || !this.showPopup) return;

    /** Store the element that had focus before opening the dialog */
    let previousActiveElement = null;

    /** Get all focusable elements inside the dialog */
    const getFocusableElements = () => {
      const focusableSelectors = [
        'button',
        '[href]',
        'input',
        'select',
        'textarea',
        '[tabindex]:not([tabindex="-1"])'
      ];
      return Array.from(popup.querySelectorAll(focusableSelectors.join(',')))
        .filter(el => {
          if (el.disabled) return false;
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });
    };

    /** Focus trap: handle Tab key navigation within dialog */
    const handleDialogKeydown = (e) => {
      if (!popup.hasAttribute('open')) return;

      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const togglePopup = (show) => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }

      if (show) {
        popup.setAttribute('open', '');
        trigger.setAttribute('aria-expanded', 'true');
        
        /** Store the element that had focus */
        previousActiveElement = document.activeElement;
        
        /** Move focus to the first focusable element in the dialog */
        requestAnimationFrame(() => {
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          } else {
            popup.setAttribute('tabindex', '-1');
            popup.focus();
          }
          
          /** Announce to screen readers */
          this.announce(`Review distribution opened. ${this.data.totalReviews} total reviews.`);
        });
      } else {
        popup.removeAttribute('open');
        trigger.setAttribute('aria-expanded', 'false');
        
        /** Return focus to the trigger element */
        if (previousActiveElement && (previousActiveElement === trigger || previousActiveElement === container)) {
          requestAnimationFrame(() => {
            trigger.focus();
          });
        }
        
        /** Announce to screen readers */
        this.announce('Review distribution closed.');
      }
    };

    const scheduleClose = () => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
      }
      this.closeTimeout = setTimeout(() => {
        togglePopup(false);
        this.closeTimeout = null;
      }, 200);
    };

    const cancelClose = () => {
      if (this.closeTimeout) {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    };

    const handleBlur = () => {
      setTimeout(() => {
        const active = document.activeElement;
        if (popup.hasAttribute('open') && !container.contains(active) && !popup.contains(active)) {
          togglePopup(false);
        }
      }, 50);
    };

    const handleKeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = popup.hasAttribute('open');
        togglePopup(!isOpen);
      } else if (e.key === 'Escape') {
        if (popup.hasAttribute('open')) {
          e.preventDefault();
          e.stopPropagation();
          togglePopup(false);
        }
      }
    };

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = popup.hasAttribute('open');
      togglePopup(!isOpen);
    };

    const handleButtonClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      togglePopup(false);

      const details = document.getElementById('product__accordion_reviews');
      const reviewBlock = document.getElementById('tab4');
      const reviewBlockContent = document.getElementById('tab4-tab');

      if (details && !details.open) {
        details.open = true;
        if (reviewBlockContent) reviewBlockContent.classList.add('is-active');
        if (reviewBlock) reviewBlock.setAttribute('aria-expanded', 'true');
      }

      if (!reviewBlock || !reviewBlockContent) {
        this.announce('Redirecting to product page to view reviews.');
        this._timeouts.push(setTimeout(() => {
          window.location.href = this.productUrl;
        }, 300));
        return;
      }

      this.announce('Scrolling to reviews section.');

      if (window.innerWidth < 1025) {
        reviewBlockContent.scrollIntoView({ behavior: 'smooth' });
        const mobileController = reviewBlockContent.querySelector('.mobile-accordion-controller');
        if (mobileController) {
          if (mobileController.getAttribute('aria-expanded') !== 'true') {
            mobileController.click();
          }
          this._timeouts.push(setTimeout(() => {
            if (mobileController.tabIndex !== -1) {
              mobileController.focus();
            } else {
              mobileController.setAttribute('tabindex', '-1');
              mobileController.focus();
            }
          }, 500));
        }
      } else {
        reviewBlock.scrollIntoView({ behavior: 'smooth' });
        /** Set focus after scroll animation completes */
        this._timeouts.push(setTimeout(() => {
          if (reviewBlock.tabIndex !== -1) {
            reviewBlock.focus();
          } else {
            reviewBlock.setAttribute('tabindex', '-1');
            reviewBlock.focus();
          }
        }, 500));
      }
    };

    container.addEventListener('mouseenter', () => {
      cancelClose();
      togglePopup(true);
    });
    container.addEventListener('mouseleave', scheduleClose);
    
    popup.addEventListener('mouseenter', cancelClose);
    popup.addEventListener('mouseleave', scheduleClose);
    
    trigger.addEventListener('click', handleClick);
    trigger.addEventListener('blur', handleBlur);
    trigger.addEventListener('keydown', handleKeydown);
    popup.addEventListener('focusin', () => {
      if (!popup.hasAttribute('open')) {
        togglePopup(true);
      }
    });
    popup.addEventListener('focusout', handleBlur);
    popup.addEventListener('keydown', handleDialogKeydown);
    popup.addEventListener('cancel', (e) => {
      e.preventDefault();
      togglePopup(false);
    });

    const readReviewsButton = this.querySelector('button[data-skip-reviews]');
    if (readReviewsButton) {
      readReviewsButton.addEventListener('click', handleButtonClick);
      readReviewsButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleButtonClick(e);
        }
      });
    }
  }

  disconnectedCallback() {
    this._timeouts.forEach(timeout => clearTimeout(timeout));
    this._timeouts = [];
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  announce(message) {
    let liveRegion = document.getElementById('trustpilot-review-announcements');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'trustpilot-review-announcements';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
    this._timeouts.push(setTimeout(() => {
      liveRegion.textContent = '';
    }, 2000));
  }
}

if (!customElements.get('trustpilot-review-summary')) {
  customElements.define('trustpilot-review-summary', TrustpilotReviewSummary);
}


/** Called by theme to init/upgrade trustpilot-review-summary elements that are not yet custom elements. */
window.initializeElements = function() {
  document.querySelectorAll('trustpilot-review-summary:not([data-initialized])').forEach((element) => {
    try {
      if (!(element instanceof TrustpilotReviewSummary)) {
        const upgraded = document.createElement('trustpilot-review-summary');
        Array.from(element.attributes).forEach(attr => upgraded.setAttribute(attr.name, attr.value));
        upgraded.className = element.className;
        if (element.parentNode) element.parentNode.replaceChild(upgraded, element);
      }
    } catch (error) {
      console.error('Trustpilot Review Summary initializeElements error:', error);
    }
  });
};
